import { Static, Type } from '@sinclair/typebox';
import { clearableOnUpdate } from './typebox-clearable';
import {
  BookingPromoIneligibilityReason,
  ProPromoCodeCustomerScope,
  ProPromoCodeDistribution,
  ProPromoCodeDistributionSchema,
  ProPromoCodeKind,
  ProPromoCodeKindSchema,
  ProPromoCodeStatus,
  ProPromoCodeTrigger,
  ProPromoCodeWindowBasis,
  ProPromotionDisplayStatus,
} from './translation_namespace/pro_promo_code_namespace';

// =============================================================================
// Codes promo pro (B2C) — remise % ou montant fixe saisie par le client final
// dans le tunnel de réservation. Miroir du mécanisme voucher (résolution au même
// endroit, snapshot dans pro_payment). Schémas CRUD pro + flux tunnel.
// =============================================================================

// Énumérations définies dans le namespace de traduction (unions libellées pour le formulaire) et
// ré-exportées ici pour les consommateurs historiques.
export { BookingPromoIneligibilityReason, ProPromoCodeDistribution, ProPromoCodeKind };

const HHMM_PATTERN = '^([01]\\d|2[0-3]):[0-5]\\d$';

// Créneau happy-hours (heure locale du pro), ex. { start: '18:00', end: '20:00' }.
export const ProPromoCodeTimeWindowSchema = Type.Object({
  start: Type.String({ pattern: HHMM_PATTERN }),
  end: Type.String({ pattern: HHMM_PATTERN }),
});
export type ProPromoCodeTimeWindowObject = Static<typeof ProPromoCodeTimeWindowSchema>;

// Même créneau, libellé pour le formulaire pro (DynamicForm : un item = deux champs `time`).
const ProPromoCodeTimeWindowFormSchema = Type.Object({
  start: Type.String({
    pattern: HHMM_PATTERN,
    title: 'INPUT_TITLE_PRO_PROMO_TIME_START',
    meta: { input_type: 'time' },
  }),
  end: Type.String({ pattern: HHMM_PATTERN, title: 'INPUT_TITLE_PRO_PROMO_TIME_END', meta: { input_type: 'time' } }),
});

// =============================================================================
// CRUD pro — schémas rendus par DynamicForm (création en modale plein écran via
// `dialog_config`, édition via `update_data`, comme les autres pages de liste).
// Les clés `title` / `placeholder` / `section_title` vivent dans DYNAMIC_FORM.
// `meta.group: 'advanced'` range le champ sous « Options avancées ».
// =============================================================================

// Normalisation serveur : trim().toUpperCase() (le pro peut saisir en minuscules).
const PROMO_CODE_PATTERN = '^[A-Za-z0-9_-]+$';
const PERSONAL_PREFIX_PATTERN = '^[A-Za-z0-9]*$';

const promoFormFields = {
  // Requis si distribution = SHARED_CODE, interdit/ignoré si PERSONAL_CODES
  // (validation conditionnelle côté service).
  code: Type.String({
    minLength: 1,
    maxLength: 64,
    pattern: PROMO_CODE_PATTERN,
    title: 'INPUT_TITLE_PRO_PROMO_CODE_CODE',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_CODE_CODE',
    meta: { visible_when: { field: 'distribution', value: ProPromoCodeDistribution.SHARED_CODE } },
  }),
  personal_code_prefix: Type.String({
    maxLength: 12,
    pattern: PERSONAL_PREFIX_PATTERN,
    title: 'INPUT_TITLE_PRO_PROMO_PERSONAL_PREFIX',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_PERSONAL_PREFIX',
    meta: { visible_when: { field: 'distribution', value: ProPromoCodeDistribution.PERSONAL_CODES } },
  }),
  label: Type.String({
    minLength: 1,
    maxLength: 255,
    title: 'INPUT_TITLE_PRO_PROMO_LABEL',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_LABEL',
  }),
  // Description publique, reprise par le site et le tunnel : 200 caractères au plus (spec Promotions § 4).
  description: Type.String({
    maxLength: 200,
    title: 'INPUT_TITLE_PRO_PROMO_DESCRIPTION',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_DESCRIPTION',
    meta: { multiline: true },
  }),

  kind: ProPromoCodeKindSchema({
    section_title: 'PRO_PROMO_SECTION_DISCOUNT',
    title: 'INPUT_TITLE_PRO_PROMO_KIND',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_KIND',
    // DynamicForm rend une union en sélection MULTIPLE par défaut : un seul type de remise.
    meta: { multiple: false, disable_search: true },
  }),
  // PERCENT : pourcentage 1-100 ; FIXED_AMOUNT_MINOR : montant fixe en centimes (> 0). Le rendu
  // (pourcentage ou prix) suit `kind` (renderer `promo_value`). Une valeur nulle est refusée par le
  // service (PROMO_CODE_INVALID_VALUE, erreur métier traduite) plutôt que par le schéma.
  value: Type.Integer({
    minimum: 0,
    title: 'INPUT_TITLE_PRO_PROMO_VALUE',
    meta: { input_type: 'promo_value' },
  }),

  // Ciblage : applies_to_all = tout le catalogue ; sinon prestations/catégories, portées par les
  // deux champs cachés que le sélecteur (`promo_prestation_targeting`) alimente.
  applies_to_all: Type.Boolean({
    title: 'INPUT_TITLE_PRO_PROMO_TARGETING',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_TARGETING',
    meta: { input_type: 'promo_prestation_targeting' },
  }),
  prestation_ids: Type.Array(Type.Integer(), { meta: { hidden: true } }),
  category_ids: Type.Array(Type.Integer(), { meta: { hidden: true } }),

  // Dates SEULES : le backend stocke `valid_from` au DÉBUT de son jour et `valid_until` à la FIN de
  // son jour (dernière milliseconde), dans le fuseau du pro — le dernier jour est inclus en entier.
  valid_from: Type.String({
    format: 'date-time',
    section_title: 'PRO_PROMO_SECTION_CONDITIONS',
    title: 'INPUT_TITLE_PRO_PROMO_VALID_FROM',
    meta: { date_only: true },
  }),
  valid_until: Type.String({
    format: 'date-time',
    title: 'INPUT_TITLE_PRO_PROMO_VALID_UNTIL',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_VALID_UNTIL',
    meta: { date_only: true },
  }),

  // Happy-hours : jours ISO 1-7 ([] = tous), créneaux { start, end } heure locale pro.
  days_of_week: Type.Array(Type.Integer({ minimum: 1, maximum: 7 }), {
    title: 'INPUT_TITLE_PRO_PROMO_DAYS_OF_WEEK',
    meta: { input_type: 'weekday_selector', hint: 'INPUT_HINT_PRO_PROMO_DAYS_OF_WEEK', group: 'advanced' },
  }),
  time_windows: Type.Array(ProPromoCodeTimeWindowFormSchema, {
    title: 'INPUT_TITLE_PRO_PROMO_TIME_WINDOWS',
    meta: { group: 'advanced' },
  }),

  max_redemptions: Type.Integer({
    minimum: 1,
    title: 'INPUT_TITLE_PRO_PROMO_MAX_REDEMPTIONS',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_UNLIMITED',
    meta: { group: 'advanced' },
  }),
  max_redemptions_per_customer: Type.Integer({
    minimum: 1,
    title: 'INPUT_TITLE_PRO_PROMO_MAX_PER_CUSTOMER',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_UNLIMITED',
    meta: { group: 'advanced' },
  }),
  min_cart_amount_minor: Type.Integer({
    minimum: 0,
    title: 'INPUT_TITLE_PRO_PROMO_MIN_CART',
    placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_MIN_CART',
    meta: { is_price: true, group: 'advanced' },
  }),

  first_time_only: Type.Boolean({ title: 'INPUT_TITLE_PRO_PROMO_FIRST_TIME_ONLY', meta: { group: 'advanced' } }),
  restore_on_cancel: Type.Boolean({ title: 'INPUT_TITLE_PRO_PROMO_RESTORE_ON_CANCEL', meta: { group: 'advanced' } }),
  is_active: Type.Boolean({ title: 'INPUT_TITLE_PRO_PROMO_IS_ACTIVE' }),
};

// Champs de la Promotion (lot 2, éditeur Marketing › Promotions). Facultatifs à l'écriture : l'ancien
// formulaire ne les envoie pas (`is_active` et `first_time_only` en tiennent lieu, écrits en miroir).
const promotionFields = {
  // AUTO : appliquée sans code (pas de code ni de codes personnels) ; CODE : saisie du client.
  trigger: Type.Enum(ProPromoCodeTrigger),
  // DRAFT : brouillon incomplet permis ; ACTIVE / PAUSED / ARCHIVED.
  status: Type.Enum(ProPromoCodeStatus),
  customer_scope: Type.Enum(ProPromoCodeCustomerScope),
  // Libellé montré aux clients (badge, récap, emails) ; `label` reste le nom interne.
  public_label: Type.String({ minLength: 1, maxLength: 60 }),
  applies_to_supplements: Type.Boolean(),
  highlight_on_site: Type.Boolean(),
  // Vide = tous les lieux.
  place_ids: Type.Array(Type.Integer()),
  // Tags des clients concernés (`customer_scope` = TAGS) : au moins un.
  tag_ids: Type.Array(Type.Integer()),
  // Lot 4 : ce que bornent les dates (défaut : le rendez-vous) ; membres concernés (vide = tous).
  window_basis: Type.Enum(ProPromoCodeWindowBasis),
  team_member_ids: Type.Array(Type.Integer()),
};

export const ProPromoCodeCreateRequestSchema = Type.Object({
  // Figée à la création : un code partagé ne devient jamais une campagne de codes personnels.
  distribution: Type.Optional(
    ProPromoCodeDistributionSchema({
      title: 'INPUT_TITLE_PRO_PROMO_DISTRIBUTION',
      placeholder: 'INPUT_PLACEHOLDER_PRO_PROMO_DISTRIBUTION',
      // Un seul type de code (cf. `kind`).
      meta: { multiple: false, disable_search: true },
    }),
  ),
  code: Type.Optional(promoFormFields.code),
  personal_code_prefix: Type.Optional(promoFormFields.personal_code_prefix),
  // Nom interne ; à défaut, le libellé public (l'un des deux est requis).
  label: Type.Optional(promoFormFields.label),
  description: Type.Optional(promoFormFields.description),
  kind: promoFormFields.kind,
  value: promoFormFields.value,
  applies_to_all: promoFormFields.applies_to_all,
  prestation_ids: Type.Optional(promoFormFields.prestation_ids),
  category_ids: Type.Optional(promoFormFields.category_ids),
  valid_from: promoFormFields.valid_from,
  valid_until: Type.Optional(promoFormFields.valid_until),
  days_of_week: Type.Optional(promoFormFields.days_of_week),
  time_windows: Type.Optional(promoFormFields.time_windows),
  max_redemptions: Type.Optional(promoFormFields.max_redemptions),
  max_redemptions_per_customer: Type.Optional(promoFormFields.max_redemptions_per_customer),
  min_cart_amount_minor: Type.Optional(promoFormFields.min_cart_amount_minor),
  first_time_only: Type.Optional(promoFormFields.first_time_only),
  restore_on_cancel: promoFormFields.restore_on_cancel,
  is_active: Type.Optional(promoFormFields.is_active),
  trigger: Type.Optional(promotionFields.trigger),
  status: Type.Optional(promotionFields.status),
  customer_scope: Type.Optional(promotionFields.customer_scope),
  public_label: Type.Optional(promotionFields.public_label),
  applies_to_supplements: Type.Optional(promotionFields.applies_to_supplements),
  highlight_on_site: Type.Optional(promotionFields.highlight_on_site),
  place_ids: Type.Optional(promotionFields.place_ids),
  tag_ids: Type.Optional(promotionFields.tag_ids),
  window_basis: Type.Optional(promotionFields.window_basis),
  team_member_ids: Type.Optional(promotionFields.team_member_ids),
});
export type ProPromoCodeCreateRequestObject = Static<typeof ProPromoCodeCreateRequestSchema>;

// Formulaire d'ÉDITION (DynamicForm, `update_data.schema`). Les champs kind/value/ciblage/code sont
// refusés côté service dès qu'un redemption existe ET que leur valeur change
// (PROMO_CODE_LOCKED_AFTER_USE). `distribution` n'est pas modifiable : porté caché pour piloter la
// visibilité code / préfixe, ignoré par l'API. Pas d'union avec `null` ici : DynamicForm rend toute
// union (`anyOf`) en liste déroulante — le corps du PUT, lui, est `ProPromoCodeUpdateRequestSchema`.
export const ProPromoCodeUpdateFormSchema = Type.Partial(
  Type.Object({
    distribution: Type.Enum(ProPromoCodeDistribution, { meta: { hidden: true } }),
    ...promoFormFields,
  }),
);
export type ProPromoCodeUpdateFormObject = Static<typeof ProPromoCodeUpdateFormSchema>;

// Corps du PUT (update partiel) : absent = inchangé ; `null` EFFACE description, date de fin,
// plafonds et panier minimum ; `prestation_ids: []` / `category_ids: []` vident la liste.
export const ProPromoCodeUpdateRequestSchema = Type.Object({
  distribution: Type.Optional(Type.Enum(ProPromoCodeDistribution, { meta: { hidden: true } })),
  code: Type.Optional(promoFormFields.code),
  personal_code_prefix: Type.Optional(promoFormFields.personal_code_prefix),
  label: Type.Optional(promoFormFields.label),
  description: clearableOnUpdate(promoFormFields.description),
  kind: Type.Optional(promoFormFields.kind),
  value: Type.Optional(promoFormFields.value),
  applies_to_all: Type.Optional(promoFormFields.applies_to_all),
  prestation_ids: Type.Optional(promoFormFields.prestation_ids),
  category_ids: Type.Optional(promoFormFields.category_ids),
  valid_from: Type.Optional(promoFormFields.valid_from),
  valid_until: clearableOnUpdate(promoFormFields.valid_until),
  days_of_week: Type.Optional(promoFormFields.days_of_week),
  time_windows: Type.Optional(promoFormFields.time_windows),
  max_redemptions: clearableOnUpdate(promoFormFields.max_redemptions),
  max_redemptions_per_customer: clearableOnUpdate(promoFormFields.max_redemptions_per_customer),
  min_cart_amount_minor: clearableOnUpdate(promoFormFields.min_cart_amount_minor),
  first_time_only: Type.Optional(promoFormFields.first_time_only),
  restore_on_cancel: Type.Optional(promoFormFields.restore_on_cancel),
  is_active: Type.Optional(promoFormFields.is_active),
  trigger: Type.Optional(promotionFields.trigger),
  status: Type.Optional(promotionFields.status),
  customer_scope: Type.Optional(promotionFields.customer_scope),
  public_label: clearableOnUpdate(promotionFields.public_label),
  applies_to_supplements: Type.Optional(promotionFields.applies_to_supplements),
  highlight_on_site: Type.Optional(promotionFields.highlight_on_site),
  place_ids: Type.Optional(promotionFields.place_ids),
  tag_ids: Type.Optional(promotionFields.tag_ids),
  window_basis: Type.Optional(promotionFields.window_basis),
  team_member_ids: Type.Optional(promotionFields.team_member_ids),
});
export type ProPromoCodeUpdateRequestObject = Static<typeof ProPromoCodeUpdateRequestSchema>;

export const ProPromoCodeSchema = Type.Object({
  pro_promo_code_id: Type.Integer(),
  code: Type.Optional(Type.String()),
  distribution: Type.Enum(ProPromoCodeDistribution),
  personal_code_prefix: Type.Optional(Type.String()),

  label: Type.String(),
  description: Type.Optional(Type.String()),

  kind: Type.Enum(ProPromoCodeKind),
  value: Type.Integer(),
  // Montant formaté, renseigné UNIQUEMENT si kind = FIXED_AMOUNT_MINOR (`fixed_amount_minor` n'est
  // fourni par le service que dans ce cas) ; le frontend affiche `${value}%` pour PERCENT.
  value_amount_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'fixed_amount_minor' } })),

  applies_to_all: Type.Boolean(),
  prestation_ids: Type.Array(Type.Integer()),
  category_ids: Type.Array(Type.Integer()),

  valid_from: Type.String({ format: 'date-time' }),
  valid_until: Type.Optional(Type.String({ format: 'date-time' })),
  valid_from_formatted: Type.String({ format: 'date-time', meta: { source_key: 'valid_from', is_short: true } }),
  valid_until_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'valid_until', is_short: true } }),
  ),

  days_of_week: Type.Array(Type.Integer()),
  time_windows: Type.Array(ProPromoCodeTimeWindowSchema),

  max_redemptions: Type.Optional(Type.Integer()),
  // Utilisations CONFIRMÉES et non restituées (même base que `has_redemptions` et la vue
  // « Utilisations ») : une utilisation seulement réservée par une pré-réservation n'y figure pas.
  current_redemptions: Type.Integer(),
  max_redemptions_per_customer: Type.Optional(Type.Integer()),
  min_cart_amount_minor: Type.Optional(Type.Integer()),
  min_cart_amount_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'min_cart_amount_minor' } }),
  ),

  first_time_only: Type.Boolean(),
  restore_on_cancel: Type.Boolean(),
  is_active: Type.Boolean(),

  trigger: Type.Enum(ProPromoCodeTrigger),
  status: Type.Enum(ProPromoCodeStatus),
  // Statut calculé (fenêtre, quota) : pastille des cartes et des « Codes actifs ».
  display_status: Type.Enum(ProPromotionDisplayStatus),
  customer_scope: Type.Enum(ProPromoCodeCustomerScope),
  public_label: Type.Optional(Type.String()),
  applies_to_supplements: Type.Boolean(),
  highlight_on_site: Type.Boolean(),
  place_ids: Type.Array(Type.Integer()),
  tag_ids: Type.Array(Type.Integer()),
  window_basis: Type.Enum(ProPromoCodeWindowBasis),
  team_member_ids: Type.Array(Type.Integer()),

  // Verrouillage édition (kind/value/ciblage) une fois un usage enregistré.
  has_redemptions: Type.Boolean(),

  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', is_short: true } }),
  ),
});
export type ProPromoCodeObject = Static<typeof ProPromoCodeSchema>;

/** Onglets de la page Promotions, et le tableau « Codes actifs ». */
export enum ProPromotionListTab {
  /** Actives, en pause ou à venir. */
  ACTIVE = 'ACTIVE',
  /** Terminées, épuisées et archivées. */
  ENDED = 'ENDED',
  DRAFT = 'DRAFT',
  /** Promotions par code, hors archivées et hors celles terminées depuis plus de 90 jours. */
  CODES = 'CODES',
}

/** Une promotion par code reste dans « Codes actifs » ce nombre de jours après sa fin. */
export const PROMOTION_CODES_ENDED_VISIBLE_DAYS = 90;

export const ProPromoCodeListResponseSchema = Type.Object({
  total: Type.Integer(),
  has_elements: Type.Boolean(),
  items: Type.Array(ProPromoCodeSchema),
  // Nombre de promotions de chaque onglet (libellés « Actives · 2 », « Brouillons · 1 »).
  tab_counts: Type.Optional(
    Type.Object({ active: Type.Integer(), ended: Type.Integer(), draft: Type.Integer(), codes: Type.Integer() }),
  ),
});
export type ProPromoCodeListResponseObject = Static<typeof ProPromoCodeListResponseSchema>;

// Une utilisation d'un code (vue « Utilisations » côté pro).
export const ProPromoRedemptionSchema = Type.Object({
  pro_promo_redemption_id: Type.Integer(),
  // Absent pour une promotion automatique (aucun code saisi).
  code: Type.Optional(Type.String()),
  label: Type.String(),
  discount: Type.String({ meta: { is_price: true, source_key: 'discount_minor' } }),

  customer_firstname: Type.Optional(Type.String()),
  customer_lastname: Type.Optional(Type.String()),
  customer_email: Type.Optional(Type.String()),

  reservation_id: Type.Optional(Type.Integer()),
  reservation_uuid: Type.Optional(Type.String()),

  is_voided: Type.Boolean(),
  redeemed_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'redeemed_at', with_time: true } }),
});
export type ProPromoRedemptionObject = Static<typeof ProPromoRedemptionSchema>;

export const ProPromoRedemptionListResponseSchema = Type.Object({
  total: Type.Integer(),
  has_elements: Type.Boolean(),
  items: Type.Array(ProPromoRedemptionSchema),
});
export type ProPromoRedemptionListResponseObject = Static<typeof ProPromoRedemptionListResponseSchema>;

// Un code personnel distribué à un client (vue « Codes personnels » côté pro).
export const ProPromoPersonalCodeSchema = Type.Object({
  pro_promo_personal_code_id: Type.Integer(),
  code: Type.String(),

  customer_firstname: Type.Optional(Type.String()),
  customer_lastname: Type.Optional(Type.String()),
  customer_email: Type.Optional(Type.String()),

  campaign_name: Type.Optional(Type.String()),

  is_used: Type.Boolean(),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
});
export type ProPromoPersonalCodeObject = Static<typeof ProPromoPersonalCodeSchema>;

export const ProPromoPersonalCodeListResponseSchema = Type.Object({
  total: Type.Integer(),
  has_elements: Type.Boolean(),
  items: Type.Array(ProPromoPersonalCodeSchema),
});
export type ProPromoPersonalCodeListResponseObject = Static<typeof ProPromoPersonalCodeListResponseSchema>;

/**
 * Statut affiché d'une promotion : brouillon, en pause et archivée tels quels ; une promotion active est
 * « à venir » avant son début, « terminée » après sa fin, « épuisée » à son quota.
 */
export function resolvePromotionDisplayStatus(
  promotion: {
    status: ProPromoCodeStatus | string;
    valid_from: Date;
    valid_until: Date | null;
    max_redemptions: number | null;
    current_redemptions: number;
  },
  now: Date,
): ProPromotionDisplayStatus {
  if (promotion.status === ProPromoCodeStatus.DRAFT) return ProPromotionDisplayStatus.DRAFT;
  if (promotion.status === ProPromoCodeStatus.ARCHIVED) return ProPromotionDisplayStatus.ARCHIVED;
  if (promotion.valid_until !== null && now > promotion.valid_until) return ProPromotionDisplayStatus.ENDED;
  if (promotion.max_redemptions !== null && promotion.current_redemptions >= promotion.max_redemptions) {
    return ProPromotionDisplayStatus.EXHAUSTED;
  }
  if (promotion.status === ProPromoCodeStatus.PAUSED) return ProPromotionDisplayStatus.PAUSED;
  if (now < promotion.valid_from) return ProPromotionDisplayStatus.SCHEDULED;
  return ProPromotionDisplayStatus.ACTIVE;
}

/** Onglet d'une promotion selon son statut affiché. */
export function resolvePromotionListTab(displayStatus: ProPromotionDisplayStatus): ProPromotionListTab {
  switch (displayStatus) {
    case ProPromotionDisplayStatus.DRAFT:
      return ProPromotionListTab.DRAFT;
    case ProPromotionDisplayStatus.ENDED:
    case ProPromotionDisplayStatus.EXHAUSTED:
    case ProPromotionDisplayStatus.ARCHIVED:
      return ProPromotionListTab.ENDED;
    default:
      return ProPromotionListTab.ACTIVE;
  }
}

// ─── Éditeur : aperçu des prix et disponibilité du code ─────────────────────

/** Promotion en cours d'édition, telle que l'aperçu la chiffre. */
export const ProPromotionPreviewRequestSchema = Type.Object({
  // Promotion éditée (exclue des avertissements de chevauchement) ; absente à la création.
  pro_promo_code_id: Type.Optional(Type.Integer()),
  kind: Type.Enum(ProPromoCodeKind),
  value: Type.Integer({ minimum: 0 }),
  applies_to_supplements: Type.Optional(Type.Boolean()),
  applies_to_all: Type.Boolean(),
  prestation_ids: Type.Optional(Type.Array(Type.Integer())),
  category_ids: Type.Optional(Type.Array(Type.Integer())),
  trigger: Type.Optional(Type.Enum(ProPromoCodeTrigger)),
  place_ids: Type.Optional(Type.Array(Type.Integer())),
  valid_from: Type.Optional(Type.String({ format: 'date-time', meta: { date_only: true } })),
  valid_until: Type.Optional(Type.String({ format: 'date-time', meta: { date_only: true } })),
  window_basis: Type.Optional(Type.Enum(ProPromoCodeWindowBasis)),
});
export type ProPromotionPreviewRequestObject = Static<typeof ProPromotionPreviewRequestSchema>;

export const ProPromotionPreviewResponseSchema = Type.Object({
  // Quelques prestations du périmètre, prix avant / après formatés par le serveur (locale du pro).
  prices: Type.Array(
    Type.Object({
      prestation_id: Type.Integer(),
      name: Type.String(),
      // Formule : « 45 min », ou libellé de la formule.
      price_label: Type.Optional(Type.String()),
      price_before: Type.String(),
      price_after: Type.String(),
    }),
  ),
  // Prestations du périmètre au-delà de l'aperçu (« + 3 autres prestations »).
  more_count: Type.Integer(),
  // Nombre de prestations du périmètre ; 0 : périmètre vide (bloquant à l'activation).
  scope_count: Type.Integer(),
  // Autres promotions automatiques actives sur une partie des mêmes prestations, sur une période commune.
  overlapping_auto: Type.Array(Type.Object({ pro_promo_code_id: Type.Integer(), label: Type.String() })),
});
export type ProPromotionPreviewResponseObject = Static<typeof ProPromotionPreviewResponseSchema>;

/** Le code est-il libre ? Pris par une autre promotion, un code personnel ou une carte cadeau / un bon. */
export enum ProPromoCodeAvailability {
  AVAILABLE = 'AVAILABLE',
  USED_BY_PROMOTION = 'USED_BY_PROMOTION',
  USED_BY_VOUCHER = 'USED_BY_VOUCHER',
}

export const ProPromoCodeAvailabilityResponseSchema = Type.Object({
  availability: Type.Enum(ProPromoCodeAvailability),
});
export type ProPromoCodeAvailabilityResponseObject = Static<typeof ProPromoCodeAvailabilityResponseSchema>;

/** Prolonger une promotion de N jours (« Prolonger de 7 jours » dans « Codes actifs »). */
export const ProPromotionExtendRequestSchema = Type.Object({
  days: Type.Integer({ minimum: 1, maximum: 365 }),
});
export type ProPromotionExtendRequestObject = Static<typeof ProPromotionExtendRequestSchema>;
